import { useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/core/helpers/cn";
import Button from "@/shared/ui/button/Button";

type Props<T extends string> = {
  variant?: 1 | 2;
  tabs: T[];
  selectedTab: T;
  i18fileName: string;
  i18Path: string;
  onClick: (tab: T) => void;
  className?: string;
  tabClassName?: string;
  activeTabClassName?: string;
  Icons?: ((props) => JSX.Element)[];
  color?: string;
};

export default function TabsNavigation<T extends string>({
  variant = 1,
  tabs,
  selectedTab,
  i18fileName,
  i18Path,
  onClick,
  className,
  tabClassName,
  activeTabClassName,
  Icons,
  color = "#70c299",
}: Props<T>) {
  const { t } = useTranslation(i18fileName);

  const [disableTransition, setDisableTransition] = useState(true);
  const [activeTabBlockStyle, setActiveTabBlockStyle] = useState({ width: 0, left: 0 });

  const hasPositionedRef = useRef(false);

  const refs = useRef<Array<HTMLButtonElement | null>>([]);

  useLayoutEffect(() => {
    const index = tabs.findIndex((tab) => tab === selectedTab);
    if (index >= 0 && refs.current[index]) {
      const activeTabElement = refs.current[index]!;
      const { offsetWidth, offsetLeft } = activeTabElement;
      setActiveTabBlockStyle({ width: offsetWidth, left: offsetLeft });

      if (!hasPositionedRef.current) {
        hasPositionedRef.current = true;
        requestAnimationFrame(() => setDisableTransition(false));
      }
    }
  }, [selectedTab, tabs]);

  return (
    <div
      className={cn(
        "relative h-[2.75rem] flex items-center w-full bg-dark-800 rounded-[0.5rem]",
        variant === 2 &&
          "gap-[1.3125rem] size-auto pb-2 bg-transparent border-b-[0.125rem] border-b-[#3A3A3A]/30 rounded-none",
        className,
      )}
    >
      {tabs.map((tab, index) => {
        const Icon = Icons?.[index];
        return (
          <button
            type="button"
            key={tab}
            ref={(el) => {
              refs.current[index] = el;
            }}
            className={cn(
              "z-1 flex justify-center items-center gap-2 h-full px-5 text-[0.6875rem] font-bold leading-none uppercase transition-all duration-300",
              variant === 1 ? "hover:text-white" : "hover:text-accent-solid",
              variant === 1 && selectedTab === tab && "text-white",
              variant === 1 && selectedTab !== tab && "text-white/50",
              variant === 2 && selectedTab === tab && "text-accent-solid",
              variant === 2 && selectedTab !== tab && "text-white/70",
              variant === 2 && "px-0",
              tabClassName,
            )}
            onClick={() => onClick(tab)}
          >
            {Icon ? <Icon className="size-4.5" /> : null}
            {t(`${i18Path}.${tab}`)}
          </button>
        );
      })}

      <Button
        className={cn(
          "absolute z-0 h-full px-0 rounded-xl duration-300",
          variant === 2 && "h-3.5 p-0",
          activeTabClassName,
        )}
        style={{
          width: `${activeTabBlockStyle.width}px`,
          transform: `translateX(${activeTabBlockStyle.left}px)`,
          transitionProperty: disableTransition ? "none" : "all",
        }}
        variant={variant === 2 ? "ghost" : "solid-gradient"}
      >
        {variant === 2 && (
          <>
            <div className="size-full bg-accent-solid/25 blur-[0.3rem]" />
            <div
              className="absolute -bottom-2 left-0 w-full h-0.5 rounded"
              style={{
                background: `linear-gradient(90deg, #3A3A3A 0%, ${color} 52.88%, #3A3A3A 100%)`,
              }}
            />
          </>
        )}
      </Button>
    </div>
  );
}
